import type { SupportedLocale } from '@/types';
import type { BffMenuItem } from '@/types/bff';
import type React from 'react';
import { login, logout } from '@/utils/auth';
import { withLocaleParam } from '@/utils/locale';
import { resolveIcon } from '@/utils/resolveIcon';
import { Button } from '@vaisala/rockhopper-vaisala-com-components/Button/index';
import { Link } from '@vaisala/rockhopper-vaisala-com-components/Link/index';
import { Menu } from '@vaisala/rockhopper-vaisala-com-components/Menu/index';
import styles from './MenuItem.module.scss';

export interface MenuItemProps {
  item: BffMenuItem;
  /** Resolved locale, forwarded to the identity provider on login as `ui_locales`. */
  locale?: SupportedLocale;
}

const VALID_BUTTON_VARIANTS = ['primary', 'secondary', 'tertiary', 'outline', 'subtle'] as const;
type ButtonVariant = (typeof VALID_BUTTON_VARIANTS)[number];

/** Validates and returns a safe button variant, defaulting to 'primary' for unknowns. */
function resolveButtonVariant(value?: string): ButtonVariant {
  if (value && (VALID_BUTTON_VARIANTS as readonly string[]).includes(value)) {
    return value as ButtonVariant;
  }
  return 'primary';
}

/**
 * Handles item activation. If the item has an `action` field (login/logout),
 * it uses the auth utility. Otherwise navigates via URL.
 */
function handleItemClick(item: BffMenuItem, locale?: SupportedLocale) {
  if (item.action === 'login') {
    login(locale);
    return;
  }
  if (item.action === 'logout') {
    logout();
    return;
  }

  const url = item.baseUrl + item.path;
  if (!url) return;

  // The destination is often a different origin, where localStorage isn't
  // readable — carry the locale in the URL so it survives the navigation.
  const localizedUrl = withLocaleParam(url, locale);

  if (item.target === '_blank') {
    window.open(localizedUrl, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = localizedUrl;
  }
}

/**
 * MenuItem — Unified menu item renderer driven by the BFF API `type` discriminator.
 *
 * All navigation is handled internally — no callbacks to the host.
 * - `button` (with buttonVariant): Renders as a styled Button CTA
 * - `button` (without buttonVariant): Renders as a Menu.Item (role="menuitem")
 * - `link`: Navigable anchor element with underline
 * - `text`: Non-interactive display element
 * - `divider`: Visual separator
 * - Unknown types: renders as a plain Menu.Item
 *
 * The `align` property controls text alignment (default: 'left').
 */
export function MenuItem({ item, locale }: Readonly<MenuItemProps>): JSX.Element | null {
  const textAlign = (item.align as 'left' | 'center' | 'right') || 'left';
  const startIcon = resolveIcon(item.startIcon);
  const endIcon = resolveIcon(item.endIcon);

  switch (item.type) {
    case 'button': {
      if (item.buttonVariant) {
        return (
          <div className={styles.buttonWrapper} style={{ textAlign }}>
            <Button
              variant={resolveButtonVariant(item.buttonVariant)}
              width="full-width"
              startIcon={startIcon}
              endIcon={endIcon}
              onClick={() => handleItemClick(item, locale)}
            >
              {item.label}
            </Button>
          </div>
        );
      }
      return (
        <Menu.Item
          startIcon={startIcon}
          endIcon={endIcon}
          onClick={() => handleItemClick(item, locale)}
        >
          {item.label}
        </Menu.Item>
      );
    }

    case 'link': {
      // If the link has an action (login/logout), use auth utility instead of href
      if (item.action) {
        return (
          <div className={styles.linkWrapper} style={{ textAlign }}>
            <Link
              href="#"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handleItemClick(item, locale);
              }}
              style={{ textDecoration: 'underline' }}
              startIcon={startIcon}
              endIcon={endIcon}
            >
              {item.label}
            </Link>
          </div>
        );
      }
      return (
        <div className={styles.linkWrapper} style={{ textAlign }}>
          <Link
            href={withLocaleParam(item.baseUrl + item.path, locale)}
            target={item.target || '_self'}
            rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
            style={{ textDecoration: 'underline' }}
            startIcon={startIcon}
            endIcon={endIcon}
          >
            {item.label}
          </Link>
        </div>
      );
    }

    case 'text':
      return (
        <p className={styles.text} style={{ textAlign }}>
          {item.label}
        </p>
      );

    case 'divider':
      return <Menu.Divider spacing="none" size="s" />;

    default:
      // Unrecognized type — render as a plain menu item per Requirement 1.7
      return (
        <Menu.Item
          startIcon={startIcon}
          endIcon={endIcon}
          onClick={() => handleItemClick(item, locale)}
        >
          {item.label}
        </Menu.Item>
      );
  }
}
